import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';

import { Card, CardStackSection } from '@origin-digital/ods-core';
import { SuccessMessage } from '@origin-digital/messages';
import { logger } from '@origin-digital/reporting-client';
import { navComplete } from '@origin-digital/event-dispatcher';

import { routes } from 'src/consts';
import { AccountBackend } from 'src/interfaces';
import { useOnce } from 'src/hooks';
import { useAppContext } from 'src/context/AppContext';
import { updateActionBar } from 'src/utils/notification';
import { isMyAccount, isLpg, isKrakenMyAccount } from 'src/utils/helpers';
import { Loading } from 'src/components/Loading';

import { MyAccountCard } from 'src/cards/MyAccountCard';
import { PersonalisedCard } from 'src/cards/PersonalisedCard';

import { EmailReceipt } from './EmailReceipt';
import { SummaryCard } from './SummaryCard';

import * as content from './content.json';

export const getContentKey = (accountType: AccountBackend) => {
  if (isMyAccount()) {
    return 'auth';
  }

  if (isKrakenMyAccount()) {
    return 'authrx';
  }

  if (accountType === AccountBackend.kraken) {
    return 'unauthrx';
  }

  return 'unauth';
};

export const SuccessScreen: React.SFC = () => {
  const { context } = useAppContext();
  const [mounting, setMounting] = useState<boolean>(true);
  const [invalid, setInvalid] = useState<boolean>(false);

  const shouldShowMyAccountCard = () =>
    !isMyAccount() && !isKrakenMyAccount() && !isLpg(context.divisionId);
  const shouldShowPersonalisedCard = () => !isKrakenMyAccount();

  useOnce(() => {
    setMounting(false);

    if (!context.receiptNumber) {
      logger.info('successScreen.setInvalid(): receiptNumber');

      return setInvalid(true);
    }

    navComplete.paymentMake({ receiptNumber: context.receiptNumber });

    updateActionBar({ mobileNavButton: 'close' });
  });

  if (mounting) {
    return <Loading />;
  }

  if (invalid) {
    return (
      <div data-id="redirect-invalid">
        <Redirect to={routes.base} />
      </div>
    );
  }

  const contentKey = getContentKey(context.backend);

  return (
    <CardStackSection data-id="payment-success">
      <Card data-id="success-message-card">
        <SuccessMessage
          heading={content.heading[contentKey]}
          text={content.body[contentKey].replace('{accountNumber}', context.contractAccountId)}
          markdown
        />
      </Card>

      <SummaryCard context={context} />

      <EmailReceipt context={context} />

      {shouldShowMyAccountCard() && <MyAccountCard />}

      {shouldShowPersonalisedCard() && <PersonalisedCard />}
    </CardStackSection>
  );
};
